import { IHttpService, IPromise, IQService, IRequestShortcutConfig } from "angular";
import { EnvironmentService } from "@gtmhub/env";
import { ICollection } from "@webapp/core/core.models";
import { IBadge, IBadgeAssignment, IExtendedBadgeAssignment, IRewardAnnouncement } from "../models/badges";

export class BadgeService {
  public static $inject = ["$http", "$q", "EnvironmentService"];

  constructor(private $http: IHttpService, private $q: IQService, private env: EnvironmentService) {}

  public getBadgeAssignments(assignmentIds: string[], assigneeIds: string[], badgeId: string): IPromise<ICollection<IBadgeAssignment>> {
    const url = this.env.getApiEndpoint("/badges/assign");
    const config: IRequestShortcutConfig = {
      params: {
        ids: assignmentIds && assignmentIds.length !== 0 ? assignmentIds.toString() : null,
        assigneeIds: assigneeIds && assigneeIds.length !== 0 ? assigneeIds.toString() : null,
        badgeId: badgeId,
      },
    };

    return this.$http.get<ICollection<IBadgeAssignment>>(url, config).then((response) => response.data);
  }

  public getBadges(): IPromise<ICollection<IBadge>> {
    const url = this.env.getApiEndpoint("/badges");

    return this.$http.get<ICollection<IBadge>>(url).then((response) => {
      return response.data;
    });
  }

  public getCurrentUserBadgeAssignments(): IPromise<IExtendedBadgeAssignment[]> {
    const url = this.env.getApiEndpoint("/badges/current");

    return this.$http.get<ICollection<IExtendedBadgeAssignment>>(url).then((response) => {
      const col = response.data;

      // the service does not populate this, so we will do it here
      for (const assignment of col.items) {
        assignment.badgeId = assignment.badge.id;
      }

      return col.items;
    });
  }

  public getBadgesToBeApproved(): IPromise<ICollection<IBadgeAssignment>> {
    const url = this.env.getApiEndpoint("/badges/approve");

    return this.$http.get<ICollection<IBadgeAssignment>>(url).then((response) => response.data);
  }

  public getAssigneeIdBadgeAssignmentsMap(assigneeIds: string[]): IPromise<Map<string, IBadgeAssignment[]>> {
    const chunkSize = 120;
    const promises: Array<IPromise<ICollection<IBadgeAssignment>>> = [];

    for (let i = 0; i < assigneeIds.length; i += chunkSize) {
      const assigneesChunk = assigneeIds.slice(i, i + chunkSize);
      promises.push(this.getBadgeAssignments([], assigneesChunk, null));
    }

    return this.$q.all(promises).then((collections) => {
      const assigneeIdBadgeAssignmentsMap: Map<string, IBadgeAssignment[]> = new Map();
      for (const badgeAssignmentCollection of collections) {
        for (const badgeAssignment of badgeAssignmentCollection.items) {
          if (badgeAssignment.status === "DIRECT_ASSIGN" || badgeAssignment.status === "APPROVED_ASSIGN") {
            for (const assigneeId of badgeAssignment.assigneeIds) {
              assigneeIdBadgeAssignmentsMap.set(assigneeId, [...(assigneeIdBadgeAssignmentsMap.get(assigneeId) || []), badgeAssignment]);
            }
          }
        }
      }

      return assigneeIdBadgeAssignmentsMap;
    });
  }

  public approveBadgeAward(badgeId: string): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/badges/approve/" + badgeId);

    return this.$http.post(url, null).then((response) => response.data);
  }

  public rejectBadgeAward(badgeId: string): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/badges/approve/" + badgeId);

    return this.$http.delete(url).then((response) => response.data);
  }

  public configureRewardAnnouncement(assignment: IBadgeAssignment): IRewardAnnouncement {
    return {
      announcementType: "badge",
      announcementTargetId: assignment.id,
      comment: assignment.comment,
      notificationStrategy: assignment.notificationStrategy,
      notify: assignment.notify,
      authorId: assignment.assignedById,
    };
  }

  public getBadge(id: string): IPromise<IBadge> {
    const url = this.env.getApiEndpoint("/badges/" + id);

    return this.$http.get<IBadge>(url).then((response) => response.data);
  }

  public createBadge(badge: IBadge): IPromise<IBadge> {
    const url = this.env.getApiEndpoint("/badges");

    return this.$http.post<IBadge>(url, badge).then((response) => response.data);
  }

  public updateBadge(badge: IBadge): IPromise<IBadge> {
    const url = this.env.getApiEndpoint("/badges/" + badge.id);

    return this.$http.patch<IBadge>(url, badge).then((response) => response.data);
  }

  public deleteBadge(badgeId: string): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/badges/" + badgeId);

    return this.$http.delete<boolean>(url).then((response) => response.data);
  }

  public unassignBadge(badgeAssignmentId: string): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/badges/assign/" + badgeAssignmentId);

    return this.$http.delete<boolean>(url).then((response) => response.data);
  }

  public assignBadge(badgeAssignment: IBadgeAssignment): IPromise<IBadgeAssignment> {
    const url = this.env.getApiEndpoint("/badges/assign");

    return this.$http.post<IBadgeAssignment>(url, badgeAssignment).then((response) => response.data);
  }
}
